import moment from "moment";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import 'react-circular-progressbar/dist/styles.css';
import { sanitiseTab } from ".";
import { ArrowForward } from "@mui/icons-material";
import { getRequest } from '../../../config';

const diligentsiaWordmark = require("../../../../images/logo.png");
const reportHeaderIllustration = require("../../../../images/reportHDRBg.png");

const InsuranceGapAnalysisReport = ({ company, tab, onReportResolved }) => {
    const getHeight = child => child.offsetHeight + parseFloat(getComputedStyle(child).marginTop) + parseFloat(getComputedStyle(child).marginBottom);
    // A4 dimensions in pixels (at 96 DPI)
    const A4_HEIGHT_PX = 1122; // 297mm at 96 DPI
    const A4_WIDTH_PX = 794; // 210mm at 96 DPI
    const PAGE_MARGIN_TOP = 32; // Reduced margin
    const PAGE_MARGIN_BOTTOM = 210; // Reduced margin
    const FOOTER_HEIGHT = 80; // Footer height including margins
    const HEADER_HEIGHT = 152; // Header height for continuation pages
    const FIRST_PAGE_HEADER_HEIGHT = 220; // Enhanced first page header
    const SAFETY_BUFFER = 30; // Extra buffer to prevent footer overlap (increased for better safety margin)
    const AVAILABLE_HEIGHT = A4_HEIGHT_PX - PAGE_MARGIN_TOP - PAGE_MARGIN_BOTTOM - FOOTER_HEIGHT - SAFETY_BUFFER;

    let [reportId, setReportId] = useState();
    let [reportData, setReportData] = useState({});
    let [fetchedAiReports, setFetchedAiReports] = useState(null);
    const user = useSelector(state => state.user);
    const createdBy = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || 'Diligentsia Team';
    // When viewing a history snapshot, show that version's saved date; otherwise use createdAt.
    // New records store lastUpdatedTime as UTC ISO-8601; legacy records stored a human-readable
    // string (saved in server local time — treat as UTC for prod backward compat).
    const historyReportDate = (() => {
        if (reportData['lastUpdatedTime']) {
            const iso = moment(reportData['lastUpdatedTime'], moment.ISO_8601, true);
            if (iso.isValid()) return iso.local().format('MMM Do YYYY, h:mm A');
            const legacy = moment.utc(reportData['lastUpdatedTime'], ['MMMM Do, YYYY  hh:mma', 'MMMM Do, YYYY hh:mma'], true);
            if (legacy.isValid()) return legacy.local().format('MMM Do YYYY, h:mm A');
        }
        if (reportData['createdAt']) return moment.utc(reportData['createdAt']).local().format('MMM Do YYYY, h:mm A');
        return '';
    })();
    const reportDate = historyReportDate;
    useEffect(() => {
        // Update reportId when tab or hash changes
        if (tab) {
            // If tab is provided, use it directly
            let sanitizedTab = sanitiseTab(tab);
            // Also remove -analyser suffix if present (for Analyser reports)
            sanitizedTab = sanitizedTab.replace(/-analyser$/, '');
            setReportId(sanitizedTab);
        } else if (window.location.hash) {
            let hash = window.location.hash.split('?')[0];
            if (hash) {
                hash = hash.replaceAll('#', '');
                let sanitizedHash = sanitiseTab(hash);
                // Also remove -analyser suffix if present
                sanitizedHash = sanitizedHash.replace(/-analyser$/, '');
                setReportId(sanitizedHash);
            }
        }
    }, [tab]);

    // Also listen to hash changes for direct navigation
    useEffect(() => {
        const handleHashChange = () => {
            if (window.location.hash && !tab) {
                let hash = window.location.hash.split('?')[0];
                if (hash) {
                    hash = hash.replaceAll('#', '');
                    let sanitizedHash = sanitiseTab(hash);
                    // Also remove -analyser suffix if present
                    sanitizedHash = sanitizedHash.replace(/-analyser$/, '');
                    setReportId(sanitizedHash);
                }
            }
        };

        // Also check on mount
        handleHashChange();

        window.addEventListener('hashchange', handleHashChange);
        return () => window.removeEventListener('hashchange', handleHashChange);
    }, [tab]);

    useEffect(() => {
        if (!company?._id) return;
        getRequest(`/company/${company._id}/reports`).then(res => {
            if (res?.['code'] == 200) setFetchedAiReports(res['body']?.['reports']?.['aiReports'] || []);
        }).catch(() => { });
    }, [company?._id]);

    useEffect(() => {
        // Use fetched aiReports (direct API) with fallback to company.aiReports
        const reportsArray = fetchedAiReports || company?.['aiReports'] || [];
        if (!reportId || !String(reportId).trim() || reportsArray.length === 0) {
            if (!reportId || !String(reportId).trim()) {
                console.log('[InsuranceGapAnalysisReport] No reportId provided or empty');
            } else {
                console.log('[InsuranceGapAnalysisReport] No reports available in company.aiReports');
            }
            setReportData({});
            return;
        }

        // Helper function to sanitize reportLabel for matching (must match Analyser/index.js sanitizeReportLabel)
        const sanitizeForMatching = (label) => {
            if (!label) return '';
            return label
                .replace(/\s+/g, '-')  // Replace spaces with dashes
                .replace(/[^a-zA-Z0-9.-]/g, '')  // Keep letters, digits, dots, dashes
                .replace(/-+/g, '-')  // Replace multiple dashes with single dash
                .replace(/^-|-$/g, '');  // Remove leading/trailing dashes
        };

        // Clean reportId - remove query params and ensure it's a string
        const cleanReportId = String(reportId || '').split('?')[0].trim();

        // Extract history entry id (if any) carried in the tab value as ?history=<id>
        let historyId = '';
        const reportIdQuery = String(reportId || '').split('?')[1];
        if (reportIdQuery) historyId = new URLSearchParams(reportIdQuery).get('history') || '';

        // Find matching report
        let foundReport = null;

        for (const item of reportsArray) {
            if (!item?.htmlReport) continue;

            // Priority 1: Try matching by sanitized reportLabel (for Analyser reports with reportLabel)
            // This should be checked first since we're now using reportLabel as the primary identifier
            if (item.reportLabel) {
                const sanitizedLabel = sanitizeForMatching(item.reportLabel);
                if (sanitizedLabel === cleanReportId || sanitizedLabel === reportId) {
                    foundReport = item;
                    break;
                }
            }

            // Priority 2: Try matching by _id (for Analyser reports using _id as identifier)
            if (item._id) {
                const itemIdStr = item._id.toString();
                if (itemIdStr === cleanReportId || itemIdStr === reportId) {
                    foundReport = item;
                    break;
                }
            }

            if (item.reportType && item.reportType.startsWith('sectionReport_')) {
                const sanitizedSectionName = item.reportType.replace('sectionReport_', '');
                const itemSanitizedName = item.sanitizedSectionName || sanitizedSectionName;
                let extractedSectionName = reportId;
                if (reportId.endsWith('-section')) {
                    extractedSectionName = reportId.replace(/-section$/, '');
                }

                if (itemSanitizedName === extractedSectionName) {
                    foundReport = item;
                    break;
                }
                if (itemSanitizedName === reportId) {
                    foundReport = item;
                    break;
                }
                if (itemSanitizedName.toLowerCase() === extractedSectionName.toLowerCase()) {
                    foundReport = item;
                    break;
                }
                if (item.reportType === `sectionReport_${reportId}` ||
                    item.reportType === `sectionReport_${extractedSectionName}`) {
                    foundReport = item;
                    break;
                }
            } else {
                // Standard report matching by name
                const itemNameSanitized = sanitiseTab(item['name']?.toLowerCase().replaceAll(" ", "-") || '');
                if (itemNameSanitized === reportId || itemNameSanitized === sanitiseTab(reportId)) {
                    foundReport = item;
                    break;
                }
            }
        }

        if (foundReport) {
            // If a history id was requested, render that saved snapshot instead of the latest version.
            let resolvedReport = foundReport;
            if (historyId && Array.isArray(foundReport['history'])) {
                const historyEntry = foundReport['history'].find(h => String(h?.['_id'] || '') === historyId);
                if (historyEntry && Array.isArray(historyEntry['htmlReport']) && historyEntry['htmlReport'].length) {
                    resolvedReport = { ...foundReport, htmlReport: historyEntry['htmlReport'], lastUpdatedTime: historyEntry['lastUpdatedTime'], isHistoryVersion: true };
                } else {
                    console.warn('[InsuranceGapAnalysisReport] History snapshot not found or has no content, showing latest:', historyId);
                }
            }

            setReportData(resolvedReport);
            if (onReportResolved) onReportResolved(foundReport['reportLabel'] || foundReport['name'] || '');
        } else {
            // Debug: Log when report is not found to help diagnose issues
            if (reportId && reportsArray.length > 0) {
                console.error('[InsuranceGapAnalysisReport] Report not found:');
            }
            setReportData({});
        }
    }, [tab, reportId, company, fetchedAiReports]);

    useEffect(() => {
        const pdfContent = document.getElementById("pdfContent");
        const wrapper = document.getElementById("pdf");

        if (!pdfContent || !wrapper || !reportData?.['htmlReport']) {
            return;
        }

        // Deduplicate htmlReport array if needed (safety check)
        let htmlReportArray = reportData['htmlReport'];
        if (Array.isArray(htmlReportArray)) {
            const seen = new Set();
            htmlReportArray = htmlReportArray.filter((item, index) => {
                if (!item || typeof item !== 'string') return false;
                const trimmed = item.trim();
                if (trimmed.length === 0) return false; // Remove empty items
                const normalized = trimmed.replace(/\s+/g, ' ');
                if (seen.has(normalized)) {
                    console.warn(`Duplicate htmlReport section at index ${index} detected and removed`);
                    return false;
                }
                seen.add(normalized);
                return true;
            });
        }

        pdfContent.classList.add("a4-pdf", "p3rem");
        wrapper.innerHTML = "";

        // Add global styles to ensure tables don't overflow
        if (!document.getElementById('table-width-fix')) {
            const style = document.createElement('style');
            style.id = 'table-width-fix';
            style.textContent = `
                #pdf table {
                    max-width: 100% !important;
                    width: 100% !important;
                    box-sizing: border-box !important;
                    table-layout: auto !important;
                    border-collapse: collapse !important;
                }
                #pdf table td, #pdf table th {
                    word-wrap: break-word;
                    overflow-wrap: break-word;
                    white-space: normal !important;
                    /* Remove max-width: 0 to allow all columns to render properly */
                    /* With table-layout: auto, columns will auto-size based on content */
                }
                #pdf .insurance-gap-report {
                    width: 210mm !important;
                    max-width: 210mm !important;
                }
                #pdf .insurance-gap-report > div {
                    max-width: 100% !important;
                    overflow: hidden !important;
                }
            `;
            document.head.appendChild(style);
        }

        // Get children from pdfContent - filter out non-report content (buttons, disclaimers, etc.)
        // Also extract all tables from containers to process them individually
        let children = [];
        const allTables = new Set(); // Track all tables to prevent duplicates

        Array.from(pdfContent.children).forEach(child => {
            // Only process elements that contain report content (not the CTA buttons or disclaimer)
            const isReportContent = child.classList.contains('insurance-section') ||
                child.classList.contains('insurance-report-content') ||
                (child.querySelector && (child.querySelector('table') || child.querySelector('.summary-container')));

            if (!isReportContent) return;

            // If child contains tables, we'll extract them separately
            // But keep the child for non-table content
            const tablesInChild = child.querySelectorAll('table');

            if (tablesInChild.length > 0) {
                // Child contains tables - we'll process tables separately
                // But keep child reference for extracting titles/summaries
                children.push(child);
            } else {
                // Child doesn't contain tables - process as regular content
                children.push(child);
            }
        });

        if (children.length === 0) {
            return;
        }

        let pageNumber = 1;
        let currentPageHeight = 0;
        let contentContainer = null;

        // Track which tables have had their titles added (to prevent duplicates across pages)
        const tableTitlesAdded = new Set();

        // Helper function to create footer
        const createFooter = (pageNum) => {
            const footer = document.createElement("div");
            footer.classList.add("tac", "fw600", "df", "jcsb", "aife", "pa", "c36", "b10", "l0", "pdfFooterCustom");
            footer.style.bottom = "10px";
            footer.style.left = "0";
            footer.style.right = "0";
            footer.style.padding = "0 0px";

            const footerLogo = document.createElement("img");
            footerLogo.src = "https://repository-diligentsia.s3.ap-northeast-1.amazonaws.com/development/miniicon-1735284051094.png";
            footerLogo.alt = "miniicon";
            footerLogo.classList.add("h40px", "w40px", "tal");

            const trademark = document.createElement("p");
            trademark.innerText = `© Diligentsia Limited ${new Date().getFullYear()}`;
            trademark.classList.add("tac", "c37", "fs12px", "fw400");

            const pageNumberElem = document.createElement("h3");
            pageNumberElem.innerText = "Page " + pageNum;
            pageNumberElem.classList.add("fw600", "c36", "fs14px");

            footer.appendChild(footerLogo);
            footer.appendChild(trademark);
            footer.appendChild(pageNumberElem);

            return footer;
        };

        // Helper function to create a new page
        const createPage = (isFirstPage = false) => {
            const page = document.createElement("div");
            page.classList.add("a4-pdf", "oyh", "bg1", "bsbb", "oh", "pr", "px3rem", "shadow", "new-report", "insurance-gap-report");

            // Add watermark
            const watermark = document.createElement("img");
            watermark.classList.add("op20", "pa");
            watermark.style.transform = "translate(-50%, -50%) rotate(30deg)";
            watermark.style.top = "50%";
            watermark.style.left = "50%";
            watermark.style.width = "297mm";
            watermark.style.zIndex = "0";
            page.appendChild(watermark);

            // Create content container
            const content = document.createElement("div");
            content.style.position = "relative";
            content.style.zIndex = "1";
            content.style.paddingBottom = (FOOTER_HEIGHT + SAFETY_BUFFER) + "px"; // Extra padding to prevent footer overlap
            content.style.width = "100%";
            content.style.maxWidth = "100%";
            content.style.boxSizing = "border-box";
            content.style.overflow = "hidden";

            // Add header for all pages
            titleHeading(content, isFirstPage);

            if (isFirstPage) {
                currentPageHeight = FIRST_PAGE_HEADER_HEIGHT;
            } else {
                currentPageHeight = HEADER_HEIGHT;
            }

            page.appendChild(content);

            // Add footer
            const footer = createFooter(pageNumber);
            page.appendChild(footer);

            wrapper.appendChild(page);
            pageNumber++;

            return { page, content };
        };

        // Helper function to check if element fits on current page
        // Use conservative check with buffer to prevent overflow
        const fitsOnPage = (elementHeight, buffer = 5) => {
            const fits = currentPageHeight + elementHeight + buffer < AVAILABLE_HEIGHT;
            return fits;
        };

        // Helper function to split large content elements
        const splitLargeElement = (element) => {
            const elementHeight = getHeight(element);

            // If element fits on page, just add it (with buffer for safety)
            if (fitsOnPage(elementHeight, 10)) {
                contentContainer.appendChild(element.cloneNode(true));
                currentPageHeight += elementHeight;
                return;
            }

            // For divs with multiple children, try to split by children
            if (element.tagName === 'DIV' && element.children.length > 0) {
                const children = Array.from(element.children);
                const wrapper = element.cloneNode(false);

                children.forEach(child => {
                    const childHeight = getHeight(child);

                    if (!fitsOnPage(childHeight, 10)) {
                        // Add current wrapper if it has content
                        if (wrapper.children.length > 0) {
                            contentContainer.appendChild(wrapper);
                        }

                        // Create new page
                        const newPage = createPage();
                        contentContainer = newPage.content;

                        // Create new wrapper
                        wrapper.innerHTML = '';
                    }

                    wrapper.appendChild(child.cloneNode(true));
                    currentPageHeight += childHeight;
                });

                // Add remaining wrapper
                if (wrapper.children.length > 0) {
                    contentContainer.appendChild(wrapper);
                }
            } else {
                // For other elements or single large elements, add to new page
                if (currentPageHeight > 0) {
                    const newPage = createPage();
                    contentContainer = newPage.content;
                }

                contentContainer.appendChild(element.cloneNode(true));
                currentPageHeight += elementHeight;
            }
        };

        // Helper function to clone table with all styles and structure preserved (without thead/tbody)
        const cloneTableStructure = (originalTable) => {
            const cloned = originalTable.cloneNode(false);

            // Copy all attributes (id, class, data-*, style, width, border, cellspacing, cellpadding, etc.)
            Array.from(originalTable.attributes).forEach(attr => {
                cloned.setAttribute(attr.name, attr.value);
            });

            // Copy inline styles explicitly - this preserves width, borders, padding, etc.
            if (originalTable.style && originalTable.style.cssText) {
                cloned.style.cssText = originalTable.style.cssText;
            }

            // Ensure table doesn't overflow page width - constrain to content area
            // Content area is 210mm (A4 width) minus padding (3rem on each side = 6rem total)
            // 3rem ≈ 48px, so 6rem ≈ 96px
            // Available width = 794px - 96px = 698px
            cloned.style.maxWidth = '100%';
            cloned.style.width = '100%';
            cloned.style.boxSizing = 'border-box';
            // Use 'auto' layout to allow all columns to render properly
            // Fixed layout might hide columns if colgroup is incorrect
            cloned.style.tableLayout = 'auto'; // Allow table to adjust column widths automatically

            // Also copy common style properties that might affect layout
            if (originalTable.style.borderCollapse) cloned.style.borderCollapse = originalTable.style.borderCollapse;
            if (originalTable.style.tableLayout && originalTable.style.tableLayout !== 'auto') {
                // Only override if original was 'fixed' - but we'll use 'auto' for better column rendering
                cloned.style.tableLayout = 'auto';
            }

            // Preserve colgroup and col elements (critical for column widths)
            // But do NOT copy thead or tbody - we'll add those separately to avoid duplication
            const colgroup = originalTable.querySelector('colgroup');
            if (colgroup) {
                // Deep clone colgroup to preserve all col elements and their attributes
                const clonedColgroup = colgroup.cloneNode(true);
                cloned.appendChild(clonedColgroup);

                // Verify all col elements are preserved
                const originalCols = colgroup.querySelectorAll('col');
                const clonedCols = clonedColgroup.querySelectorAll('col');
                if (originalCols.length !== clonedCols.length) {
                    console.warn(`Colgroup clone mismatch: original=${originalCols.length}, cloned=${clonedCols.length}`);
                }
            }

            return cloned;
        };

        // Helper function to apply table wrapper styling (for individual table container)
        // Each table gets its own wrapper with border - no grouping of multiple tables
        const applyTableWrapperStyles = (wrapper) => {
            if (!wrapper) return;

            // Apply border with 20px radius and #C9C9C9 color - this is for ONE table only
            wrapper.style.border = '1px solid #C9C9C9';
            wrapper.style.borderRadius = '20px';
            wrapper.style.overflow = 'hidden';
            wrapper.style.width = '100%';
            wrapper.style.maxWidth = '100%';
            wrapper.style.boxSizing = 'border-box';
            wrapper.style.marginBottom = '2rem'; // Add spacing between tables
            wrapper.style.padding = '0'; // No padding on wrapper - padding will be on inner content

            // Ensure table inside doesn't exceed wrapper width and A4 constraints
            const table = wrapper.querySelector('table');
            if (table) {
                // Critical: Ensure table never overflows A4 width (210mm = 794px at 96 DPI)
                // Account for page padding (3rem = 48px on each side = 96px total)
                // Available width = 794px - 96px = 698px
                table.style.maxWidth = '100%';
                table.style.width = '100%';
                table.style.boxSizing = 'border-box';
                table.style.tableLayout = 'auto'; // Allow flexible column widths

                // Prevent table from exceeding container
                table.style.overflow = 'hidden';
                table.style.wordWrap = 'break-word';
                table.style.overflowWrap = 'break-word';
            }
        };

        // Helper function to apply c15 class to title headings and subheadings
        const applyTitleStyles = (titleElement) => {
            if (!titleElement) return;

            // Apply c15 class to all headings and text elements in the title
            const headings = titleElement.querySelectorAll('h1, h2, h3, h4, h5, h6');
            headings.forEach(heading => {
                if (!heading.classList.contains('c15')) {
                    heading.classList.add('c15');
                }
            });

            // Also check if the title element itself is a heading
            if (titleElement.tagName.match(/^H[1-6]$/)) {
                if (!titleElement.classList.contains('c15')) {
                    titleElement.classList.add('c15');
                }
            }

            // Apply to any text elements that might be subtitles
            const textElements = titleElement.querySelectorAll('p, span, div');
            textElements.forEach(el => {
                // Only apply if it's likely a subtitle (short text, not too long)
                if (el.textContent.trim().length < 200 && el.textContent.trim().length > 0) {
                    if (!el.classList.contains('c15')) {
                        el.classList.add('c15');
                    }
                }
            });
        };

        const createTitleElement = (headingText, subheadingText) => {
            if (!headingText && !subheadingText) return null;

            const container = document.createElement('div');
            container.style.marginBottom = '1.5rem';

            if (headingText) {
                const heading = document.createElement('h2');
                heading.textContent = headingText;
                container.appendChild(heading);
            }

            if (subheadingText) {
                const subtitle = document.createElement('p');
                subtitle.textContent = subheadingText;
                container.appendChild(subtitle);
            }

            return container;
        };

        const inferTitleFromTableStructure = (table) => {
            if (!table) return null;

            // First, try to identify by table position (if it's the third table, it's likely Technical Policy Schedule)
            const allTables = pdfContent ? Array.from(pdfContent.querySelectorAll('table')) : [];
            const tableIndex = allTables.indexOf(table);

            // Try to identify by header columns first
            const headerCells = table.querySelectorAll('thead th');
            let headerTexts = Array.from(headerCells).map(cell => cell.textContent.trim().toLowerCase());

            if (headerTexts.length === 0) {
                const firstRow = table.querySelector('tr');
                if (firstRow) {
                    headerTexts = Array.from(firstRow.querySelectorAll('th, td')).map(cell => cell.textContent.trim().toLowerCase());
                }
            }

            // Function to check if keywords match (more flexible matching)
            const containsKeywords = (keywords, requireAll = false) => {
                if (requireAll) {
                    return keywords.every(keyword =>
                        headerTexts.some(text => text.includes(keyword))
                    );
                } else {
                    // Check if any keyword matches
                    return keywords.some(keyword =>
                        headerTexts.some(text => text.includes(keyword))
                    );
                }
            };

            // Technical Policy Schedule: has Basis, Limit, Deductible, Territory columns
            // More flexible matching - check for key terms
            if (headerTexts.length > 0) {
                const hasTechnicalColumns = (
                    containsKeywords(['basis'], true) &&
                    (containsKeywords(['limit'], true) || containsKeywords(['lim'], true)) &&
                    (containsKeywords(['deductible'], true) || containsKeywords(['excess'], true)) &&
                    (containsKeywords(['territory'], true) || containsKeywords(['geographic'], true))
                );

                if (hasTechnicalColumns) {
                    return createTitleElement('Technical Policy Schedule', 'Detailed coverage specifications');
                }

                // Policy Schedule: has Insurer, Premium, Status columns
                const hasPolicyColumns = (
                    containsKeywords(['insurer'], true) &&
                    (containsKeywords(['premium'], true) || containsKeywords(['prem'], true)) &&
                    (containsKeywords(['status'], true))
                );

                if (hasPolicyColumns) {
                    return createTitleElement('Policy Schedule', 'Current insurance cover');
                }

                // Gap Analysis: has gap-related keywords
                if (containsKeywords(['gap'], false) || containsKeywords(['potential', 'improvement'], false)) {
                    return createTitleElement('Gap Analysis', 'Potential policy improvements identified through risk assessment');
                }
            }

            // Fallback: Use table position to infer title
            // First table (index 0) = Gap Analysis
            // Second table (index 1) = Policy Schedule  
            // Third table (index 2) = Technical Policy Schedule
            if (tableIndex === 0) {
                return createTitleElement('Gap Analysis', 'Potential policy improvements identified through risk assessment');
            } else if (tableIndex === 1) {
                return createTitleElement('Policy Schedule', 'Current insurance cover');
            } else if (tableIndex === 2) {
                return createTitleElement('Technical Policy Schedule', 'Detailed coverage specifications');
            }

            return null;
        };

        // Helper function to ensure header row has correct styling
        const applyTableHeaderStyles = (thead) => {
            if (!thead) return;

            // Apply background color #F0F2FD to header row
            const headerRows = thead.querySelectorAll('tr');
            headerRows.forEach(row => {
                row.style.backgroundColor = '#F0F2FD';

                // Ensure header cells use c15 class
                const headerCells = row.querySelectorAll('th');
                headerCells.forEach(cell => {
                    if (!cell.classList.contains('c15')) {
                        cell.classList.add('c15');
                    }
                });
            });
        };

        // Helper function to find table title and summary-container from the table's context
        const findTableTitleAndSummary = (table) => {
            // Check parent and siblings for title (could be a heading with subtitle)
            let current = table.parentElement;
            let titleElement = null;
            let summaryContainer = null;
            let titleContainer = null;

            // Common table title patterns to look for
            const titlePatterns = [
                /Gap Analysis/i,
                /Policy Schedule/i,
                /Technical Policy Schedule/i,
                /Policy.*Schedule/i,
                /Coverage.*Specification/i,
                /Detailed coverage specifications/i
            ];

            // Function to check a container for title elements and summary-container
            const findTitleAndSummaryInContainer = (container, tableRef) => {
                if (!container) return { title: null, summary: null };

                const tableIndex = Array.from(container.children).indexOf(tableRef);
                if (tableIndex > 0) {
                    // Check if there's a heading followed by a subtitle/description and summary-container
                    // Look back up to 10 elements to find title, summary-container, etc.
                    let previousIdx = tableIndex - 1;
                    let titleElements = [];
                    let summaryElements = [];
                    let foundTitleContent = false;
                    let foundSummaryContent = false;

                    // Look for heading elements (H1-H6) or divs containing headings, and summary-containers
                    while (previousIdx >= 0 && previousIdx >= tableIndex - 10) {
                        const sibling = container.children[previousIdx];
                        if (sibling) {
                            const siblingText = sibling.textContent.trim();
                            const isHeading = sibling.tagName.match(/^H[1-6]$/);
                            const hasHeading = sibling.querySelector('h1, h2, h3, h4, h5, h6');
                            const hasText = siblingText !== '';

                            // Check if this is a summary-container
                            const isSummaryContainer = sibling.classList.contains('summary-container') ||
                                sibling.querySelector('.summary-container') ||
                                (sibling.tagName === 'DIV' && sibling.querySelector('.summary-box'));

                            // Check if this element contains title-like text
                            const containsTitleText = titlePatterns.some(pattern => pattern.test(siblingText));

                            // Check if element is likely a title element
                            const isLikelyTitle = isHeading ||
                                hasHeading ||
                                sibling.classList.contains('table-title') ||
                                containsTitleText ||
                                (hasText && siblingText.length < 400 && siblingText.length > 0 && !isSummaryContainer);

                            // If it's a table, stop looking (we've gone too far)
                            if (sibling.tagName === 'TABLE') {
                                break;
                            }

                            if (isSummaryContainer) {
                                // Found summary-container - add it to summary elements
                                summaryElements.unshift(sibling);
                                foundSummaryContent = true;
                            } else if (isLikelyTitle) {
                                titleElements.unshift(sibling);
                                foundTitleContent = true;
                            } else if ((foundTitleContent || foundSummaryContent) && siblingText.length === 0) {
                                // Skip empty elements if we've already found content
                                previousIdx--;
                                continue;
                            } else if (foundTitleContent && foundSummaryContent) {
                                // Found both title and summary, stop looking
                                break;
                            } else if (foundTitleContent && !foundSummaryContent) {
                                // Found title but not summary yet, continue looking
                                previousIdx--;
                                continue;
                            } else {
                                // Haven't found title yet, continue looking
                                previousIdx--;
                                continue;
                            }
                        }
                        previousIdx--;
                    }

                    // Build result object with title and summary
                    let result = { title: null, summary: null };

                    // Process title elements
                    if (titleElements.length > 0) {
                        if (titleElements.length > 1) {
                            const titleContainer = document.createElement('div');
                            titleContainer.style.marginBottom = '1.5rem';
                            titleElements.forEach(el => {
                                titleContainer.appendChild(el.cloneNode(true));
                            });
                            result.title = titleContainer;
                        } else {
                            const singleEl = titleElements[0];
                            if (singleEl.querySelector('h1, h2, h3, h4, h5, h6') &&
                                (singleEl.querySelector('p') || singleEl.textContent.includes('\n'))) {
                                result.title = singleEl;
                            } else {
                                const prevSibling = singleEl.previousElementSibling;
                                if (prevSibling && prevSibling.textContent.trim().length < 300 &&
                                    !prevSibling.querySelector('table') &&
                                    !prevSibling.classList.contains('summary-container')) {
                                    const titleContainer = document.createElement('div');
                                    titleContainer.style.marginBottom = '1.5rem';
                                    titleContainer.appendChild(prevSibling.cloneNode(true));
                                    titleContainer.appendChild(singleEl.cloneNode(true));
                                    result.title = titleContainer;
                                } else {
                                    result.title = singleEl;
                                }
                            }
                        }
                    }

                    // Process summary elements
                    if (summaryElements.length > 0) {
                        if (summaryElements.length > 1) {
                            const summaryWrapper = document.createElement('div');
                            summaryWrapper.style.marginBottom = '1.5rem';
                            summaryElements.forEach(el => {
                                summaryWrapper.appendChild(el.cloneNode(true));
                            });
                            result.summary = summaryWrapper;
                        } else {
                            result.summary = summaryElements[0];
                        }
                    }

                    return result;
                }
                return { title: null, summary: null };
            };

            // First, try finding in direct parent
            let result = findTitleAndSummaryInContainer(current, table);
            titleElement = result.title;
            summaryContainer = result.summary;

            // If not found, try parent's parent (in case table is nested in a wrapper)
            if ((!titleElement && !summaryContainer) && current && current.parentElement) {
                result = findTitleAndSummaryInContainer(current.parentElement, current);
                if (!titleElement) titleElement = result.title;
                if (!summaryContainer) summaryContainer = result.summary;
            }

            // If still not found, try finding in pdfContent directly
            // Check all children of pdfContent to find the container that has our table
            if ((!titleElement && !summaryContainer) && pdfContent) {
                // Find which child of pdfContent contains this table
                for (let i = 0; i < pdfContent.children.length; i++) {
                    const child = pdfContent.children[i];
                    if (child.contains(table) || child === table) {
                        // Found the container/wrapper that has our table
                        // Check previous siblings of this container
                        if (i > 0) {
                            const titleElements = [];
                            const summaryElements = [];
                            for (let j = i - 1; j >= Math.max(0, i - 10); j--) {
                                const sibling = pdfContent.children[j];
                                if (sibling) {
                                    const text = sibling.textContent.trim();
                                    const hasTitlePattern = titlePatterns.some(pattern => pattern.test(text));
                                    const hasHeading = sibling.tagName.match(/^H[1-6]$/) ||
                                        sibling.querySelector('h1, h2, h3, h4, h5, h6');

                                    const isSummaryContainer = sibling.classList.contains('summary-container') ||
                                        sibling.querySelector('.summary-container') ||
                                        (sibling.tagName === 'DIV' && sibling.querySelector('.summary-box'));

                                    // If it's another table, stop looking
                                    if (sibling.tagName === 'TABLE' || sibling.querySelector('table')) {
                                        break;
                                    }

                                    // Check if this could be a title element or summary-container
                                    if (isSummaryContainer) {
                                        summaryElements.unshift(sibling);
                                    } else if (hasTitlePattern || hasHeading ||
                                        (text.length < 400 && text.length > 0)) {
                                        titleElements.unshift(sibling);
                                    } else if (titleElements.length > 0 || summaryElements.length > 0) {
                                        // Found elements, stop at non-matching element
                                        break;
                                    }
                                }
                            }

                            // Process title elements
                            if (titleElements.length > 0 && !titleElement) {
                                if (titleElements.length > 1) {
                                    titleContainer = document.createElement('div');
                                    titleContainer.style.marginBottom = '1.5rem';
                                    titleElements.forEach(el => {
                                        titleContainer.appendChild(el.cloneNode(true));
                                    });
                                    titleElement = titleContainer;
                                } else {
                                    titleElement = titleElements[0];
                                }
                            }

                            // Process summary elements
                            if (summaryElements.length > 0 && !summaryContainer) {
                                if (summaryElements.length > 1) {
                                    const summaryWrapper = document.createElement('div');
                                    summaryWrapper.style.marginBottom = '1.5rem';
                                    summaryElements.forEach(el => {
                                        summaryWrapper.appendChild(el.cloneNode(true));
                                    });
                                    summaryContainer = summaryWrapper;
                                } else {
                                    summaryContainer = summaryElements[0];
                                }
                            }

                            if (titleElement || summaryContainer) {
                                break; // Found elements, stop searching
                            }
                        }
                        break; // Found table container, stop searching
                    }
                }
            }

            // If no title found, infer from table structure
            if (!titleElement) {
                titleElement = inferTitleFromTableStructure(table);
            }

            // Return both title and summary-container
            return { title: titleElement, summary: summaryContainer };
        };

        // Helper function to find table title (backward compatibility)
        const findTableTitle = (table) => {
            const result = findTableTitleAndSummary(table);
            return result.title;
        };

        // Helper function to get a unique identifier for a table
        const getTableId = (table) => {
            // Use table's position in the document or a unique attribute
            const tableIndex = Array.from(pdfContent.querySelectorAll('table')).indexOf(table);
            const tableParent = table.parentElement;
            const parentIndex = tableParent ? Array.from(pdfContent.children).indexOf(tableParent) : -1;
            return `table-${parentIndex}-${tableIndex}`;
        };

        // Helper function to process table elements
        const processTable = (table) => {

            // Get both title and summary-container
            let { title: tableTitle, summary: tableSummary } = findTableTitleAndSummary(table);
            if (!tableTitle) {
                tableTitle = inferTitleFromTableStructure(table);
            }

            const tableId = getTableId(table);
            // Create a temporary container to measure table elements accurately
            const tempContainer = document.createElement('div');
            tempContainer.style.position = 'absolute';
            tempContainer.style.left = '-9999px';
            tempContainer.style.top = '0';
            tempContainer.style.width = table.offsetWidth + 'px';
            tempContainer.style.padding = '0';
            tempContainer.style.margin = '0';
            wrapper.appendChild(tempContainer);

            // Clone the table to preserve structure
            const originalTable = table.cloneNode(true);
            tempContainer.appendChild(originalTable);

            // Get only data rows from tbody elements (exclude thead rows)
            const thead = originalTable.querySelector('thead');
            const tbodyElements = originalTable.querySelectorAll('tbody');
            const rows = [];

            tbodyElements.forEach(tbody => {
                Array.from(tbody.querySelectorAll('tr')).forEach(tr => {
                    // Store reference to row - it will be cloned later with all its cells intact
                    // Verify row has cells before adding
                    const cells = tr.querySelectorAll('td, th');
                    if (cells.length === 0) {
                        console.warn('Found row with no cells:', tr);
                    } else {
                        rows.push(tr);
                    }
                });
            });

            // If no tbody exists, get all rows but exclude those in thead
            if (rows.length === 0) {
                const allRows = Array.from(originalTable.querySelectorAll('tr'));
                allRows.forEach(tr => {
                    // Only include rows that are NOT inside a thead
                    if (!thead || !thead.contains(tr)) {
                        rows.push(tr);
                    }
                });
            }

            // Verify all rows have the same number of cells (to detect missing columns)
            if (rows.length > 0 && thead) {
                // Get expected cell count from header
                const headerRow = thead.querySelector('tr');
                const expectedCellCount = headerRow ? headerRow.querySelectorAll('th, td').length : 0;

                // Check first few rows to verify column count
                rows.slice(0, 3).forEach((row, idx) => {
                    const cellCount = row.querySelectorAll('td, th').length;

                    if (cellCount !== expectedCellCount && expectedCellCount > 0) {
                        // If cells are missing, check if they're empty or actually missing
                        if (cellCount < expectedCellCount) {
                            console.error(`Row ${idx} is MISSING ${expectedCellCount - cellCount} cells!`);
                        }
                    }
                });
            }

            let tableHeader = null;
            let tableHeaderHeight = 0;

            // Extract and measure header if exists
            if (thead) {
                tableHeader = thead.cloneNode(true);

                // Verify all header cells are preserved
                const headerRows = tableHeader.querySelectorAll('tr');
                headerRows.forEach((headerRow, idx) => {
                    const headerCells = headerRow.querySelectorAll('th, td');
                });

                // Apply header styling before measuring
                applyTableHeaderStyles(tableHeader);

                const tempHeaderTable = cloneTableStructure(table);
                tempHeaderTable.appendChild(tableHeader);
                tempContainer.appendChild(tempHeaderTable);
                // Force a reflow to get accurate height
                tempHeaderTable.offsetHeight;
                tableHeaderHeight = getHeight(tempHeaderTable);
                tempContainer.removeChild(tempHeaderTable);
            }

            // Measure each row height accurately (only data rows, not header rows)
            // IMPORTANT: Measure rows incrementally to get accurate heights that account for table layout
            // When rows are measured together, column widths distribute more accurately
            const rowHeights = [];

            // Create a measurement table with header for accurate column width distribution
            const measurementTable = cloneTableStructure(table);
            if (thead) {
                const tempHeader = thead.cloneNode(true);
                applyTableHeaderStyles(tempHeader);
                measurementTable.appendChild(tempHeader);
            }
            const measurementTbody = document.createElement('tbody');
            measurementTable.appendChild(measurementTbody);
            tempContainer.appendChild(measurementTable);

            // Measure rows incrementally - add one row at a time and measure the difference
            let previousTableHeight = getHeight(measurementTable);

            rows.forEach((row, rowIdx) => {
                const rowClone = row.cloneNode(true);
                measurementTbody.appendChild(rowClone);

                // Force reflow to get accurate measurement
                measurementTable.offsetHeight;

                const newTableHeight = getHeight(measurementTable);
                const rowHeight = newTableHeight - previousTableHeight;

                // Store the incremental height (what this row adds to the table)
                rowHeights.push(Math.max(rowHeight, 0)); // Ensure non-negative

                previousTableHeight = newTableHeight;
            });

            // Clean up measurement table
            tempContainer.removeChild(measurementTable);

            // Clean up temp container
            wrapper.removeChild(tempContainer);

            // Now process rows for pagination - append tables incrementally for accurate measurement
            let currentTable = cloneTableStructure(table);

            let tbody = document.createElement('tbody');
            let hasRowsInCurrentTable = false;
            let currentTableHeight = 0;

            // Add header to first table if it exists
            if (tableHeader) {
                const clonedHeader = tableHeader.cloneNode(true);
                applyTableHeaderStyles(clonedHeader);
                currentTable.appendChild(clonedHeader);
                hasRowsInCurrentTable = true;
                currentTableHeight = tableHeaderHeight;
                // Note: We don't add table header to currentPageHeight here because
                // currentTableHeight already accounts for it, and we'll add it when we append the table
            }

            // Track if we've already rendered the title and summary for this table
            let titleRendered = tableTitlesAdded.has(tableId);
            let summaryRendered = false;

            // Helper to measure title height if needed
            const measureTitleHeight = () => {
                if (!tableTitle || titleRendered) return 0;

                // Create a temporary title element to measure
                const tempTitle = tableTitle.cloneNode(true);
                tempTitle.style.position = 'absolute';
                tempTitle.style.left = '-9999px';
                tempTitle.style.top = '0';
                tempTitle.style.visibility = 'hidden';
                tempTitle.style.width = (A4_WIDTH_PX - 96) + 'px'; // Account for page padding (3rem each side = 48px * 2 = 96px)
                contentContainer.appendChild(tempTitle);
                const titleHeight = getHeight(tempTitle) + 16; // Add margin-bottom (1rem = 16px)
                contentContainer.removeChild(tempTitle);
                return titleHeight;
            };

            // Pre-calculate wrapper overhead for first table segment
            // These are fixed overhead values that don't change per row
            // Add small buffer to account for any spacing variations
            const wrapperPadding = 32; // 1rem top + 1rem bottom = 32px
            const wrapperBorder = 2; // 1px top + 1px bottom = 2px  
            const wrapperMargin = 32; // 2rem margin bottom = 32px
            const wrapperOverheadBuffer = 5; // Small buffer for spacing variations and border radius
            const baseWrapperOverhead = wrapperPadding + wrapperBorder + wrapperMargin + wrapperOverheadBuffer;
            const titleHeight = measureTitleHeight();
            const firstTableWrapperOverhead = baseWrapperOverhead + titleHeight;

            // Process each row - check if it fits BEFORE adding it
            rows.forEach((row, index) => {
                const rowHeight = rowHeights[index];

                // Calculate what the height would be if we add this row
                const currentTableHeightWithNewRow = currentTableHeight + rowHeight;

                // Only add wrapper overhead if this is the first segment of the table on this page
                // (first segment needs wrapper, padding, border, title, margin)
                const wrapperOverhead = hasRowsInCurrentTable ? 0 : firstTableWrapperOverhead;

                // Calculate total height if we add this row
                // Use more conservative safety buffer (10px) to account for measurement inaccuracies, rounding, and spacing
                const rowSafetyBuffer = 10;
                const estimatedHeightIfAdded = currentPageHeight + currentTableHeightWithNewRow + wrapperOverhead + rowSafetyBuffer;

                // If this would exceed available height, save current table and start new page
                // Use strict comparison (>) to be more conservative and prevent overflow
                // Only break if we have rows in current table (don't break on empty table)
                if (estimatedHeightIfAdded > AVAILABLE_HEIGHT && hasRowsInCurrentTable && tbody.children.length > 0) {
                    // Save current table segment
                    currentTable.appendChild(tbody);

                    // Create wrapper for THIS table only - each table gets its own border
                    const tableWrapper = document.createElement('div');
                    applyTableWrapperStyles(tableWrapper);

                    // Create inner content container for proper spacing
                    const tableContentWrapper = document.createElement('div');
                    tableContentWrapper.style.padding = '1rem';
                    tableContentWrapper.style.width = '100%';
                    tableContentWrapper.style.boxSizing = 'border-box';

                    if (tableTitle && !titleRendered) {
                        const titleClone = tableTitle.cloneNode(true);
                        titleClone.setAttribute('data-table-title', tableId);
                        titleClone.style.marginBottom = '1rem';
                        titleClone.style.paddingLeft = '0';
                        titleClone.style.paddingTop = '0';
                        // Apply c15 class to title elements
                        applyTitleStyles(titleClone);
                        tableContentWrapper.appendChild(titleClone);
                        tableTitlesAdded.add(tableId);
                        titleRendered = true;
                    }

                    // Add table to content wrapper
                    tableContentWrapper.appendChild(currentTable);
                    // Add content wrapper to table wrapper (which has the border)
                    tableWrapper.appendChild(tableContentWrapper);
                    contentContainer.appendChild(tableWrapper);

                    // CRITICAL: Measure ACTUAL rendered height instead of using estimate
                    // Add small buffer to account for any rounding or measurement variations
                    const actualWrapperHeight = getHeight(tableWrapper) + 2; // Small buffer for safety
                    currentPageHeight += actualWrapperHeight;

                    // Create new page
                    const newPage = createPage();
                    contentContainer = newPage.content;

                    // Start new table with header - preserve all styles and structure
                    // IMPORTANT: Do NOT add title on continuation pages
                    currentTable = cloneTableStructure(table);
                    tbody = document.createElement('tbody');
                    hasRowsInCurrentTable = false;
                    currentTableHeight = 0;

                    // Add header to new table (but NOT the title - title only on first page)
                    if (tableHeader) {
                        // Deep clone the header to preserve ALL header cells
                        const clonedHeader = tableHeader.cloneNode(true);

                        // Verify header cells are preserved
                        const headerCells = clonedHeader.querySelectorAll('th, td');

                        applyTableHeaderStyles(clonedHeader);
                        currentTable.appendChild(clonedHeader);
                        currentTableHeight = tableHeaderHeight;
                        hasRowsInCurrentTable = true;
                        // Note: currentPageHeight is already set correctly by createPage()
                        // Header height is included in currentTableHeight and will be part of wrapper measurement
                    }
                }

                // Add row to current table (this will always fit because we checked above)
                // Clone row with deep=true to preserve all cells and their content, attributes, and styles
                const clonedRow = row.cloneNode(true);

                // Ensure all cells are preserved - verify column count matches header
                const originalCells = row.querySelectorAll('td, th');
                const clonedCells = clonedRow.querySelectorAll('td, th');
                const headerCellCount = tableHeader ? tableHeader.querySelector('tr')?.querySelectorAll('th, td').length : 0;

                if (originalCells.length !== clonedCells.length) {
                    console.error(`Row ${index} cell count mismatch after clone: original=${originalCells.length}, cloned=${clonedCells.length}`);
                }

                // Verify all cell attributes are preserved (colspan, rowspan, width, styles, classes)
                originalCells.forEach((originalCell, cellIdx) => {
                    const clonedCell = clonedCells[cellIdx];
                    if (clonedCell) {
                        // Verify colspan and rowspan are preserved
                        if (originalCell.hasAttribute('colspan') && !clonedCell.hasAttribute('colspan')) {
                            clonedCell.setAttribute('colspan', originalCell.getAttribute('colspan'));
                        }
                        if (originalCell.hasAttribute('rowspan') && !clonedCell.hasAttribute('rowspan')) {
                            clonedCell.setAttribute('rowspan', originalCell.getAttribute('rowspan'));
                        }
                        // Verify cell content is preserved
                        if (originalCell.textContent.trim() !== clonedCell.textContent.trim()) {
                            console.warn(`Row ${index}, Cell ${cellIdx}: content may differ (original="${originalCell.textContent.trim().substring(0, 20)}", cloned="${clonedCell.textContent.trim().substring(0, 20)}")`);
                        }
                    }
                });

                if (headerCellCount > 0 && clonedCells.length !== headerCellCount) {
                    console.error(`Row ${index} has ${clonedCells.length} cells but header has ${headerCellCount} cells!`);
                }

                // Verify cells have content (some might be empty but should still exist)
                clonedCells.forEach((cell, cellIdx) => {
                    if (!cell) {
                        console.error(`Row ${index} is missing cell at index ${cellIdx}!`);
                    }
                });

                tbody.appendChild(clonedRow);
                currentTableHeight += rowHeight;
                hasRowsInCurrentTable = true;
            });

            // Append remaining table if it has rows
            if (hasRowsInCurrentTable && tbody.children.length > 0) {
                currentTable.appendChild(tbody);

                // Wrap table in a container with proper styling - each table gets its own border
                const tableWrapper = document.createElement('div');
                applyTableWrapperStyles(tableWrapper);

                // Create inner content container for proper spacing
                const tableContentWrapper = document.createElement('div');
                tableContentWrapper.style.padding = '1rem';
                tableContentWrapper.style.width = '100%';
                tableContentWrapper.style.boxSizing = 'border-box';

                // Add table title ONLY if it hasn't been added yet
                if (tableTitle && !titleRendered) {
                    const titleClone = tableTitle.cloneNode(true);
                    titleClone.setAttribute('data-table-title', tableId);
                    titleClone.style.marginBottom = '1rem';
                    titleClone.style.paddingLeft = '0';
                    titleClone.style.paddingTop = '0';
                    // Apply c15 class to title elements
                    applyTitleStyles(titleClone);
                    tableContentWrapper.appendChild(titleClone);
                    tableTitlesAdded.add(tableId);
                    titleRendered = true;
                }

                // Add table to content wrapper
                tableContentWrapper.appendChild(currentTable);
                // Add content wrapper to table wrapper (which has the border)
                tableWrapper.appendChild(tableContentWrapper);
                contentContainer.appendChild(tableWrapper);

                // CRITICAL: Measure ACTUAL rendered height instead of using estimate
                // Add small buffer to account for any rounding or measurement variations
                const actualWrapperHeight = getHeight(tableWrapper) + 2; // Small buffer for safety
                currentPageHeight += actualWrapperHeight;
            }
        };

        // Create first page
        const firstPage = createPage(true);
        contentContainer = firstPage.content;

        // Track processed tables to prevent duplicates
        const processedTableIds = new Set();

        // Helper function to get a unique identifier for a table element
        const getTableUniqueId = (table) => {
            // Use table's content signature as unique ID
            const tableContent = table.outerHTML.replace(/\s+/g, ' ').trim();
            // Create a hash-like identifier from content
            let hash = 0;
            for (let i = 0; i < Math.min(tableContent.length, 100); i++) {
                const char = tableContent.charCodeAt(i);
                hash = ((hash << 5) - hash) + char;
                hash = hash & hash; // Convert to 32-bit integer
            }
            return `table-${Math.abs(hash)}`;
        };

        // Helper function to extract and process all tables from a container
        // Each table is processed individually with its own border wrapper
        const extractAndProcessTables = (container) => {
            if (!container) return;

            // Find all tables in the container (both direct children and nested)
            const allTablesInContainer = container.querySelectorAll('table');

            if (allTablesInContainer.length === 0) return;

            // Process each table individually with its own border
            // The table's parent context is preserved for title/summary detection
            allTablesInContainer.forEach((table) => {
                const tableId = getTableUniqueId(table);

                // Skip if this table was already processed
                if (processedTableIds.has(tableId)) {
                    console.warn(`Duplicate table detected and skipped: ${tableId}`);
                    return;
                }

                processedTableIds.add(tableId);

                // Process this table individually with its own border
                // The processTable function will find the title/summary from the table's context
                processTable(table);
            });
        };

        // Process all children
        children.forEach((child, index) => {
            const childHeight = getHeight(child);
            const tableElement = child.tagName === 'TABLE' ? child : child.querySelector('table');

            if (tableElement) {
                // If child is a direct table, process it individually
                if (child.tagName === 'TABLE') {
                    const tableId = getTableUniqueId(child);
                    if (!processedTableIds.has(tableId)) {
                        processedTableIds.add(tableId);
                        processTable(child);
                    } else {
                        console.warn(`Duplicate direct table detected and skipped at index ${index}`);
                    }
                } else {
                    // Child contains table(s) - extract and process each table individually
                    // Each table will get its own border wrapper
                    // Do NOT clone the entire child as it might contain multiple tables
                    extractAndProcessTables(child);

                    // Note: Titles and summaries are found by processTable() from the table's context
                    // So we don't need to separately process non-table content here
                    // The table's title/summary will be included in its own border wrapper
                }
            } else if (childHeight > AVAILABLE_HEIGHT) {
                // Handle very large elements that need splitting (non-table elements)
                splitLargeElement(child);
            } else {
                // Regular element (no tables) - check if it fits on current page
                if (!fitsOnPage(childHeight, 10)) {
                    // Create new page
                    const newPage = createPage();
                    contentContainer = newPage.content;
                }

                contentContainer.appendChild(child.cloneNode(true));
                currentPageHeight += childHeight;
            }
        });

        // Clean up - ensure pdfContent is completely hidden after processing
        wrapper.classList.remove("op0", "pen");
        pdfContent.classList.remove("a4", "p3rem");
        // Ensure pdfContent is hidden and doesn't interfere with display
        pdfContent.style.display = 'none';
        pdfContent.style.visibility = 'hidden';
        pdfContent.style.position = 'absolute';
        pdfContent.style.left = '-9999px';
    }, [reportData]);


    const titleHeading = (page, isFirstPage = false) => {
        if (isFirstPage) {
            const headerWrapper = document.createElement("div");
            headerWrapper.style.display = "flex";
            headerWrapper.style.justifyContent = "space-between";
            headerWrapper.style.alignItems = "flex-start";
            headerWrapper.style.background = "#f5f7ff";
            headerWrapper.style.padding = "40px 48px";
            headerWrapper.style.borderRadius = "24px";
            headerWrapper.style.margin = "-32px -48px 24px -48px";
            headerWrapper.style.position = "relative";
            headerWrapper.style.overflow = "hidden";
            headerWrapper.style.border = "1px solid rgba(37, 99, 235, 0.12)";
            headerWrapper.style.boxShadow = "0 18px 45px rgba(79, 97, 190, 0.12)";
            headerWrapper.style.backgroundImage = `url(${reportHeaderIllustration})`;
            headerWrapper.style.backgroundRepeat = "no-repeat";
            headerWrapper.style.backgroundPosition = "calc(100% - 32px) center";
            headerWrapper.style.backgroundSize = "auto 105%";

            const headerLeft = document.createElement("div");
            headerLeft.style.display = "flex";
            headerLeft.style.flexDirection = "column";
            headerLeft.style.alignItems = "flex-start";
            headerLeft.style.gap = "2px";

            const reportTitle = document.createElement("h1");
            // Use dynamic report name from reportData, fallback to "Insurance Gap Analysis Report" for insurance reports
            const reportName = reportData?.name || (reportData?.reportType === 'insuranceGapAnalysis' ? 'Insurance Gap Analysis Report' : 'Section Report');
            reportTitle.textContent = reportName;
            reportTitle.style.fontSize = "1.8rem";
            reportTitle.style.fontWeight = "600";
            reportTitle.style.color = "#111827";
            reportTitle.style.margin = "0";
            reportTitle.style.width = "100%";

            const companyName = document.createElement("p");
            companyName.textContent = (company?.['cName'] || '').toUpperCase();
            companyName.style.fontSize = "1.5rem";
            companyName.style.fontStyle = "italic";
            companyName.style.fontWeight = "400";
            companyName.style.letterSpacing = "0.08em";
            companyName.style.color = "#4b5563";
            companyName.style.margin = "0";
            companyName.style.textAlign = "left";
            companyName.style.width = "100%";

            const metaContainer = document.createElement("div");
            metaContainer.style.marginTop = "12px";
            metaContainer.style.display = "flex";
            metaContainer.style.flexDirection = "column";
            metaContainer.style.alignItems = "flex-start";
            metaContainer.style.gap = "6px";

            const createdByLine = document.createElement("p");
            createdByLine.innerHTML = `Created by: <span style="font-weight:600;">${createdBy}</span>`;
            createdByLine.style.margin = "0";
            createdByLine.style.textAlign = "left";
            createdByLine.style.width = "100%";
            createdByLine.style.fontSize = "0.9rem";
            createdByLine.style.color = "#1f2937";

            const reportDateLine = document.createElement("p");
            if (historyReportDate) {
                reportDateLine.innerHTML = `Report Date: <span style="font-weight:600;">${reportDate}</span>`;
            }
            reportDateLine.style.margin = "0";
            reportDateLine.style.fontSize = "0.9rem";
            reportDateLine.style.color = "#1f2937";

            metaContainer.appendChild(createdByLine);
            metaContainer.appendChild(reportDateLine);

            const sectionLabel = reportData?.reportLabel && reportData.reportLabel !== reportName ? reportData.reportLabel : null;
            if (sectionLabel) reportTitle.textContent = `${reportName} — ${sectionLabel}`;
            headerLeft.appendChild(reportTitle);
            headerLeft.appendChild(companyName);
            headerLeft.appendChild(metaContainer);

            const headerRight = document.createElement("div");
            headerRight.style.display = "flex";
            headerRight.style.flexDirection = "column";
            headerRight.style.alignItems = "flex-end";

            const logo = document.createElement("img");
            logo.src = diligentsiaWordmark;
            logo.alt = "Diligentsia";
            logo.style.height = "32px";
            logo.style.objectFit = "contain";

            headerRight.appendChild(logo);

            headerWrapper.appendChild(headerLeft);
            headerWrapper.appendChild(headerRight);

            page.appendChild(headerWrapper);
        } else {
            const continuationHeader = document.createElement("div");
            continuationHeader.style.display = "flex";
            continuationHeader.style.justifyContent = "space-between";
            continuationHeader.style.alignItems = "baseline";
            continuationHeader.style.marginBottom = "16px";

            const leftGroup = document.createElement("div");
            leftGroup.style.display = "flex";
            leftGroup.style.flexDirection = "column";

            const title = document.createElement("p");
            title.style.fontWeight = "600";
            title.style.fontSize = "1rem";
            title.style.color = "#1f2937";
            title.style.width = "100%";
            title.style.textAlign = "left";
            // Use dynamic report name from reportData, fallback to "Insurance Gap Analysis Report" for insurance reports
            const reportName = reportData?.name || (reportData?.reportType === 'insuranceGapAnalysis' ? 'Insurance Gap Analysis Report' : 'Section Report');
            const continuationSectionLabel = reportData?.reportLabel && reportData.reportLabel !== reportName ? reportData.reportLabel : null;
            title.textContent = continuationSectionLabel ? `${reportName} — ${continuationSectionLabel}` : reportName;

            const companyLine = document.createElement("p");
            companyLine.style.fontSize = "0.85rem";
            companyLine.style.color = "#4b5563";
            companyLine.style.marginTop = "4px";
            companyLine.style.textAlign = "left";
            companyLine.style.width = "100%";
            companyLine.textContent = company?.['cName'] || '';

            leftGroup.appendChild(title);
            leftGroup.appendChild(companyLine);

            const dateSpan = document.createElement("p");
            dateSpan.style.fontSize = "0.85rem";
            dateSpan.style.color = "#4b5563";
            dateSpan.style.textAlign = "left";
            dateSpan.style.width = "100%";
            dateSpan.textContent = reportDate;

            leftGroup.appendChild(dateSpan);


            const headerRight = document.createElement("div");
            headerRight.style.display = "flex";
            headerRight.style.flexDirection = "column";
            headerRight.style.alignItems = "flex-end";

            const logo = document.createElement("img");
            logo.src = diligentsiaWordmark;
            logo.alt = "Diligentsia";
            logo.style.height = "32px";
            logo.style.objectFit = "contain";

            headerRight.appendChild(logo);
            continuationHeader.appendChild(leftGroup);
            continuationHeader.appendChild(headerRight);
            page.appendChild(continuationHeader);
        }

        return page;
    };

    const showReportContent = () => {
        // Deduplicate htmlReport array to prevent duplicate rendering
        let htmlReportArray = reportData?.['htmlReport'];
        if (Array.isArray(htmlReportArray)) {
            const seen = new Set();
            htmlReportArray = htmlReportArray.filter((item, index) => {
                if (!item || typeof item !== 'string') return false;
                const trimmed = item.trim();
                if (trimmed.length === 0) return false; // Remove empty items
                const normalized = trimmed.replace(/\s+/g, ' ');
                if (seen.has(normalized)) {
                    console.warn(`Duplicate htmlReport section at index ${index} detected and removed in showReportContent`);
                    return false;
                }
                seen.add(normalized);
                return true;
            });
        }

        return (
            <>
                {htmlReportArray?.map((item, idx) => {
                    return <div
                        key={`insurance-report-${idx}`}
                        className={`insurance-section insurance-report-content ${(company['subscription'] === 'Free' && company['cCategory'] === "client" && idx > 1) ? 'blur-disabled' : ''}`}
                        dangerouslySetInnerHTML={{ __html: `${item}` }}
                        style={{
                            fontSize: '11px',
                            lineHeight: '1.4',
                            color: '#1f2937',
                            marginBottom: idx < htmlReportArray.length - 1 ? '0.5rem' : '0',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                            maxWidth: '100%'
                        }}
                    />
                })}
            </>
        );
    };

    const showEmptyState = () => {
        return (
            <div className="df jcc aic fdc py4rem">
                <div style={{
                    textAlign: 'center',
                    padding: '3rem',
                    background: '#f9fafb',
                    borderRadius: '16px',
                    maxWidth: '500px'
                }}>
                    <div style={{
                        fontSize: '4rem',
                        marginBottom: '1rem'
                    }}></div>
                    <h3 style={{
                        fontSize: '1.5rem',
                        fontWeight: '600',
                        color: '#1f2937',
                        marginBottom: '0.75rem'
                    }}>No Report Available</h3>
                    <p style={{
                        fontSize: '1rem',
                        color: '#6b7280'
                    }}>Generate your section report to see comprehensive analysis and recommendations</p>
                </div>
            </div>
        );
    };

    return <>
        {company && reportData?.['htmlReport'] && <>
            {/* Paginated Content (includes header on first page) */}
            <div id="pdf" className={`op0 pen ma td250 dg gg2rem jcc sectionWiseReport`}></div>

            <div id="pdfContent" className="bsbb w0 h0 oh op0 pen db pr" style={{ width: "210mm" }}>
                {showReportContent()}
                <div className="df aic">
                    <a
                        className="bg4 c1 w300 px1rem py0-5rem br50px df aic jcsb fs14px cp mr1rem tdnone"
                        href="https://koalendar.com/e/meet-with-mark-bernstein"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <img src={require('../../../../images/telephone.png')} alt="cfo-icon" style={{ width: "16px", height: '16px' }} className="mr0-5rem" />
                        Book a Call with CFO
                        <ArrowForward className="c1 ml0-5rem" />
                    </a>
                    <a
                        className="bg4 c1 w300 px1rem py0-5rem br50px df aic jcsb fs14px cp tdnone"
                        href="https://koalendar.com/e/meet-with-mark-bernstein"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <img src={require('../../../../images/telephone.png')} alt="cfo-icon" style={{ width: "16px", height: '16px' }} className="mr0-5rem" />
                        Book a Call with Insurer
                    </a>
                </div>

                <div className="mt1-5rem">
                    <p className="c15 fw500 mb1rem fs14px">This report is for guidance only.</p>
                    <p className="fs12px" style={{ color: '#505050', lineHeight: '2.5' }}>
                        This Report has been prepared by Diligensia for the exclusive use of the named client. It is based on information provided as at the date of issue and is intended solely to assist in identifying risks and opportunities. This report was generated by AI and may be inaccurate.  You are urged to check all report content. Diligentsia accepts no responsibility or liability for any errors, omissions, or delays, nor for any actions taken in reliance on this report. The contents do not constitute legal or financial advice, and professional advice should always be sought. This report may not be relied upon by any third party to whom the report is shown and Diligensia accepts no duty of care or liability to any third party who may obtain access to it.
                    </p>
                </div>
            </div>

            {/* Upgrade Banner */}
            {(company['subscription'] === 'Free' && company['cCategory'] === "client") && (
                <div className="mt3rem tac pa" style={{ bottom: '100px', left: '50%', transform: 'translateX(-50%)', zIndex: 10 }}>
                    <div style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        padding: '2rem 3rem',
                        borderRadius: '16px',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                        backdropFilter: 'blur(10px)'
                    }}>
                        <h3 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '0.75rem' }}>
                            🔒 Unlock Complete Analysis
                        </h3>
                        <p style={{ fontSize: '1rem', opacity: '0.95', marginBottom: '1.5rem' }}>
                            Upgrade to PRO or LITE to access the full Insurance Gap Analysis Report with detailed recommendations
                        </p>
                        <button style={{
                            background: 'white',
                            color: '#667eea',
                            padding: '0.75rem 2rem',
                            borderRadius: '8px',
                            border: 'none',
                            fontSize: '1rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'transform 0.2s'
                        }}>
                            Upgrade Now
                        </button>
                    </div>
                </div>
            )}

        </>}

        {company && !reportData?.['htmlReport'] && showEmptyState()}
    </>;
}

export default InsuranceGapAnalysisReport;